import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as Sentry from "@sentry/react";
import { setUser } from "../store/authSlice";
import { H1Title } from "../components/typography/typography";
import { AppButton } from "../components/buttons/button";
import AuthGeneralInput from "../components/input/authinput";
import AuthService from "../services/authService";
import { Success, Error as ToastError } from "../components/toast/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faShieldAlt } from "@fortawesome/free-solid-svg-icons";

export default function Profile() {
  const user = useSelector(state => state.auth.user);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const refreshUser = async () => {
    setLoading(true);
    try {
      const { data } = await AuthService.getMe();
      dispatch(setUser(data.data.user));
      Sentry.setUser({ id: data.data.user._id, email: data.data.user.email, role: data.data.user.role });
      Success('Profile refreshed');
    } catch { ToastError('Failed to refresh'); }
    finally { setLoading(false); }
  };

  if (!user) return null;

  return (
    <div className="space-y-6 max-w-2xl">
      <H1Title size="sm">My Profile</H1Title>

      {/* Profile Info */}
      <div className="bg-white rounded-xl border border-grey-200 p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-primary-bg rounded-full flex items-center justify-center text-xl font-bold text-primary">
            {user.firstName?.[0]}{user.lastName?.[0]}
          </div>
          <div>
            <p className="text-lg font-bold">{user.firstName} {user.lastName}</p>
            <p className="text-sm text-grey-667">{user.email}</p>
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium mt-1 inline-block ${
              user.role === 'admin' ? 'bg-red-500/10 text-red-500'
              : user.role === 'judge' ? 'bg-orange-500/10 text-orange-dark'
              : 'bg-primary-bg text-primary'
            }`}>{user.role}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-grey-98">First Name</p>
            <p className="text-sm font-semibold">{user.firstName}</p>
          </div>
          <div>
            <p className="text-xs text-grey-98">Last Name</p>
            <p className="text-sm font-semibold">{user.lastName}</p>
          </div>
          <div>
            <p className="text-xs text-grey-98">Email</p>
            <p className="text-sm font-semibold">{user.email}</p>
          </div>
          <div>
            <p className="text-xs text-grey-98">Phone</p>
            <p className="text-sm font-semibold">{user.phone || 'Not set'}</p>
          </div>
          {user.eligibility && (
            <>
              <div>
                <p className="text-xs text-grey-98">Status</p>
                <p className="text-sm font-semibold">{user.eligibility.status?.replace('_', ' ')}</p>
              </div>
              <div>
                <p className="text-xs text-grey-98">Institution</p>
                <p className="text-sm font-semibold">{user.eligibility.institution || 'Not set'}</p>
              </div>
            </>
          )}
        </div>

        <div className="mt-4">
          <AppButton onClick={refreshUser} variant="outline" fullWidth={false} size="sm" className="px-4" loading={loading}>
            <FontAwesomeIcon icon={faUser} className="mr-1" /> Refresh Profile
          </AppButton>
        </div>
      </div>

      {/* Account Info */}
      <div className="bg-white rounded-xl border border-grey-200 p-6">
        <h3 className="font-bold mb-3">
          <FontAwesomeIcon icon={faShieldAlt} className="mr-2 text-primary" /> Account
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-grey-98">Email Verified</p>
            <p className={`text-sm font-semibold ${user.emailVerified ? 'text-green' : 'text-red-500'}`}>
              {user.emailVerified ? 'Yes' : 'No'}
            </p>
          </div>
          <div>
            <p className="text-xs text-grey-98">Account Status</p>
            <p className={`text-sm font-semibold ${user.status === 'active' ? 'text-green' : 'text-red-500'}`}>
              {user.status}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
